import { Suspense } from "react";
import { notFound } from "@tanstack/react-router";
import homeStyles from "../styles/platform-home.css?url";
import type { StructuredData } from "../../src/lib/structured-data";
import { withStylesheet } from "../lib/head";
import { readyLazy, readyOnServer } from "../lib/ready-lazy";
import { platformPageHead, platformShellFrom } from "./head";
import { loadPlatformHome } from "./home";
import type { PlatformHome as PlatformHomeData } from "./home.server";

// The homepage's sections, film and scripts load only on the platform host, never on creator sites.
const MarketingHome = readyLazy(() => import("./home/marketing-home"));

/** The story a homepage address tells (?flow=league), as the index route's loader dependency. */
export function homeFlow(search: unknown) {
  const flow = (search as { flow?: unknown } | undefined)?.flow;
  return typeof flow === "string" ? flow : undefined;
}

/** Drate's homepage data for one story, or the everyday one. */
export async function loadHome(flow: string | undefined) {
  // On the server the sections load beside the data, so the page renders whole (lib/ready-lazy.ts).
  const [data] = await Promise.all([
    loadPlatformHome({ data: flow ?? null }),
    readyOnServer(MarketingHome),
  ]);
  if (!data) throw notFound();
  return data;
}

/**
 * The homepage's head: its title and description (a story's own), Drate's structured data and
 * the page's stylesheet. The film's poster, the first thing it paints, is an image in the page
 * with high fetch priority, so it needs no preload of its own.
 */
export function platformHomeHead(
  matches: readonly { loaderData?: unknown; routeId: string }[],
  data: PlatformHomeData,
) {
  const head = platformPageHead(platformShellFrom(matches), {
    pathname: "/",
    title: data.title,
    description: data.description,
    // A story's address previews its own title; its canonical page is the everyday one.
    canonicalPath: "/",
    structuredData: JSON.parse(data.structuredData) as StructuredData,
  });
  return withStylesheet(head, homeStyles);
}

/** Drate's homepage; a change of story mounts it afresh, as a new page would. */
export function PlatformHome({ data }: { data: PlatformHomeData }) {
  return (
    <Suspense fallback={null}>
      <MarketingHome key={data.flow ?? ""} data={data} />
    </Suspense>
  );
}
